import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { InvestmentInstructionSubmission } from '../../../../models/submissions/investment-instruction-submission';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { PortalService } from '../../../../services/portal/portal.service';
import { AccountTypeIdEnum } from '../../../../models/enum/shared/account-type-id.enum';

@Component({
  selector: 'app-investment-instruction-submission-instruction-changes',
  templateUrl: './investment-instruction-submission-instruction-changes.component.html',
  styleUrls: ['./investment-instruction-submission-instruction-changes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InvestmentInstructionSubmissionInstructionChangesComponent implements OnChanges {
  @Input() submission!: InvestmentInstructionSubmission;

  private portalService = inject(PortalService);

  private _submission = new BehaviorSubject<InvestmentInstructionSubmission | null>(null);
  public submission$ = this._submission.pipe(filter((sub): sub is InvestmentInstructionSubmission => sub !== null));

  public descriptionText$ = combineLatest([this.portalService.isInternal$, this.submission$]).pipe(
    map(([isInternal, submission]) => this.resolveDescriptionText(isInternal, submission.accountTypeId))
  );

  private resolveDescriptionText(isInternal: boolean, accountTypeId: AccountTypeIdEnum): string {
    switch (accountTypeId) {
      case AccountTypeIdEnum.VariableBenefit:
        return isInternal
          ? $localize`How the future deposits will be distributed among the funds.`
          : $localize`How your future deposits will be distributed among the funds.`;
      default:
        return isInternal
          ? $localize`How the upcoming contributions will be distributed among the funds.`
          : $localize`How your upcoming contributions will be distributed among the funds.`;
    }
  }

  public previousInvestmentInstructions$ = this.submission$.pipe(
    map(submission => submission.previous.investmentInstructions),
    map(instructions => instructions.slice().sort((a, b) => a.fundId - b.fundId))
  );

  public newInvestmentInstructions$ = this.submission$.pipe(
    map(submission => submission.new.investmentInstructions),
    map(instructions => instructions.slice().sort((a, b) => a.fundId - b.fundId))
  );

  public investmentInstructionsHaveChanged$ = this.newInvestmentInstructions$.pipe(
    map(newInstructions => !!newInstructions.length)
  );

  public forcedColumns$ = combineLatest([this.previousInvestmentInstructions$, this.newInvestmentInstructions$]).pipe(
    map(([previous, current]) => Math.max(previous.length, current.length))
  );

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.submission) {
      this._submission.next(this.submission);
    }
  }
}
